import React, { FC, useCallback, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { useAuth } from 'contexts/authContext';
import i18n from 'locale';
import { Step, StepPair, StepsCommonData, StepsPairData } from 'api';
import { Steps } from 'primereact/steps';
import { MenuItem } from 'primereact/menuitem';
import { classNames } from 'primereact/utils';
import { usePanel } from 'contexts/panelContext';
import { ProgressPairSteps, useProgressSteps } from 'hooks/useProgressSteps';
import { capitalizeWord, slugify } from 'utils/helpers';
import { Box, BoxSection } from 'components/_new/Box';
import { Icon } from 'components/_new/Icon';
import { Pair } from 'types/Pair';
import { KnowledgeBaseArticleContentItemMeta } from 'types/KnowledgeBase';
import { ReactComponent as PapersSvg } from '../../../assets/icons/new/papers.svg';
import { ReactComponent as FileSignSvg } from '../../../assets/icons/new/file-sign.svg';
import { ReactComponent as QuestionMarkSquareSvg } from '../../../assets/icons/new/question-mark-square.svg';
import { ReactComponent as CommunicationSvg } from '../../../assets/icons/new/communication.svg';
import { ReactComponent as UserPlusSvg } from '../../../assets/icons/new/user-plus.svg';
import { ReactComponent as UserSvg } from '../../../assets/icons/new/user.svg';
import { ReactComponent as BookSvg } from '../../../assets/icons/new/book.svg';
import { ReactComponent as FileTextWriteSvg } from '../../../assets/icons/new/file-text-write.svg';
import { ReactComponent as FileCheckSvg } from '../../../assets/icons/new/file-check.svg';
import { ReactComponent as FileChecklistSvg } from '../../../assets/icons/new/file-checklist.svg';
import { ReactComponent as ArrowCircleSvg } from '../../../assets/icons/new/arrow-circle.svg';
import { Button } from '../../_new/Button';
import SubPageTitle from '../panel/SubPageTitle';
import './ProgressSteps.scss';

type NewQuickLinksForPairProps = {
	pairSteps: ProgressPairSteps;
	mini?: boolean;
	// pair: Pick<Pair, 'id'> & { name: string };
	// secondStage: boolean;
};

const NewQuickLinksForPair = (props: NewQuickLinksForPairProps) => {
	const {
		pairSteps: { pairId, label, secondStage, activeStep, steps },
		mini,
	} = props;
	const stepsLength = steps?.length || 0;

	const { t } = useTranslation();
	const navigate = useNavigate();

	const { panelScopePath, todosData, panelType } = usePanel();

	const pairTodos =
		todosData.pair.find((pairTodo) => pairTodo.items.some((item) => item.variables?.pairId === pairId))?.items ||
		[];

	const hasSessionAlert = pairTodos.some((todo) => todo.type === 'arrange_first_session_date');
	const hasContractAlert = pairTodos.some((todo) => todo.type === 'contract_initial_sign_required');
	const hasMenteePlanAlert = pairTodos.some((todo) => todo.type === 'contract_goals_sign_required');
	const hasFinalReportAlert = pairTodos.some((todo) => todo.type === `final_report_mentor`);
	const hasEvaluationSurveyRequiredAlert = pairTodos.some((todo) => todo.type === `evaluation_survey_required`);
	const isReportDownloaded = pairTodos.some(
		(todo) => todo.type === 'final_report_mentee' && todo.variables?.isReportDownloaded
	);
	const hasProgressStepsAlert = stepsLength >= 5 ? activeStep >= stepsLength - 1 && !isReportDownloaded : false;

	if (mini) {
		return (
			<Box variant="white-bordered" slim full>
				<BoxSection horizontal header={label}>
					<div className="w-full flex flex-row flex-wrap gap-2 md:gap-4">
						<Button
							renderBefore={
								hasSessionAlert ? (
									<Icon name="exclamation-circle" className="hidden sm:flex text-md text-primary" />
								) : undefined
							}
							icon={() => <PapersSvg />}
							iconClassName="icon-container icon-container-rm text-primary"
							label={t('userPanel.dashboard.quickLinks.mentoringSessions')}
							className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
							variant={hasSessionAlert ? 'new-purple' : 'transparent'}
							onClick={() => {
								navigate({ pathname: `${panelScopePath}/pair/${pairId}/session` });
							}}
							noPad
						/>
						{!secondStage && (
							<Button
								renderBefore={
									hasContractAlert ? (
										<Icon
											name="exclamation-circle"
											className="hidden sm:flex text-md text-primary"
										/>
									) : undefined
								}
								icon={() => <FileSignSvg />}
								iconClassName="icon-container icon-container-rm text-primary"
								label={t('userPanel.dashboard.quickLinks.mentoringContract')}
								className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
								variant={hasContractAlert ? 'new-purple' : 'transparent'}
								onClick={() => {
									navigate({ pathname: `${panelScopePath}/pair/${pairId}/contract` });
								}}
								noPad
							/>
						)}
						<Button
							renderBefore={
								hasMenteePlanAlert ? (
									<Icon name="exclamation-circle" className="hidden sm:flex text-md text-primary" />
								) : undefined
							}
							icon={() => <FileTextWriteSvg />}
							iconClassName="icon-container icon-container-rm text-primary"
							label={t('userPanel.dashboard.quickLinks.menteeDevelopmentPlan')}
							className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
							variant={hasMenteePlanAlert ? 'new-purple' : 'transparent'}
							onClick={() => {
								navigate({ pathname: `${panelScopePath}/pair/${pairId}/mentee-plan` });
							}}
							noPad
						/>
						{!secondStage && (
							<Button
								icon={() => <CommunicationSvg />}
								iconClassName="icon-container icon-container-rm text-primary"
								label={t('userPanel.dashboard.quickLinks.menteeDetails')}
								className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
								variant="transparent"
								onClick={() => {
									navigate({ pathname: `${panelScopePath}/pair/${pairId}/pair-details` });
								}}
								noPad
							/>
						)}
						{secondStage && (
							<>
								{panelType === 'mentor' && (
									<Button
										renderBefore={
											hasFinalReportAlert ? (
												<Icon
													name="exclamation-circle"
													className="hidden sm:flex text-md text-primary"
												/>
											) : undefined
										}
										icon={() => <FileChecklistSvg />}
										iconClassName="icon-container icon-container-rm text-primary"
										label={t('userPanel.dashboard.quickLinks.finalReport')}
										className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
										variant="transparent"
										onClick={() => {
											navigate({ pathname: `${panelScopePath}/pair/${pairId}/final-report` });
										}}
										noPad
									/>
								)}
								{panelType === 'mentee' && (
									<Button
										renderBefore={
											hasEvaluationSurveyRequiredAlert ? (
												<Icon
													name="exclamation-circle"
													className="hidden sm:flex text-md text-primary"
												/>
											) : undefined
										}
										icon={() => <ArrowCircleSvg />}
										iconClassName="icon-container icon-container-rm text-primary"
										label={t('userPanel.dashboard.quickLinks.goalsEvaluation')}
										className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
										variant="transparent"
										onClick={() => {
											navigate({
												pathname: `${panelScopePath}/pair/${pairId}/session/last/evaluation-mentee`,
												hash: '#content',
											});
										}}
										noPad
									/>
								)}
								<Button
									renderBefore={
										hasProgressStepsAlert ? (
											<Icon
												name="exclamation-circle"
												className="hidden sm:flex text-md text-primary"
											/>
										) : undefined
									}
									icon={() => <FileCheckSvg />}
									iconClassName="icon-container icon-container-rm text-primary"
									label={t('userPanel.dashboard.quickLinks.processSummary')}
									className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
									variant="transparent"
									onClick={() => {
										navigate({
											pathname: `${panelScopePath}/pair/${pairId}/process-summary`,
										});
									}}
									noPad
								/>
							</>
						)}
					</div>
				</BoxSection>
			</Box>
		);
	}

	return (
		<div className="flex flex-row flex-wrap gap-2 md:gap-4 px-1">
			<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/session` }} className="flex-1-mhalf">
				<Box
					className="relative h-full"
					variant={hasSessionAlert ? 'white-rose-bordered' : 'white-bordered'}
					slim
					full
					hoverable
				>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<PapersSvg className="text-rose" />
						</span>
						<span>{t('userPanel.dashboard.quickLinks.mentoringSessions')}</span>
					</BoxSection>
					{hasSessionAlert && (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					)}
				</Box>
			</Link>
			{!secondStage && (
				<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/contract` }} className="flex-1-mhalf">
					<Box
						className="relative h-full"
						variant={hasContractAlert ? 'white-rose-bordered' : 'white-bordered'}
						slim
						full
						hoverable
					>
						<BoxSection contentClassName="justify-content-center align-items-center text-center">
							<span className="icon-container icon-container-lg">
								<FileSignSvg className="text-primary" />
							</span>
							<span>{t('userPanel.dashboard.quickLinks.mentoringContract')}</span>
						</BoxSection>
						{hasContractAlert && (
							<div className="box-warning-icon">
								<Icon name="exclamation-circle" className="warning-icon-color" />
							</div>
						)}
					</Box>
				</Link>
			)}
			<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/mentee-plan` }} className="flex-1-mhalf">
				<Box
					className="relative h-full"
					variant={hasMenteePlanAlert ? 'white-rose-bordered' : 'white-bordered'}
					slim
					full
					hoverable
				>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<FileTextWriteSvg className="text-primary" />
						</span>
						<span>{t('userPanel.dashboard.quickLinks.menteeDevelopmentPlan')}</span>
					</BoxSection>
					{hasMenteePlanAlert && (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					)}
				</Box>
			</Link>
			{!secondStage && (
				<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/pair-details` }} className="flex-1-mhalf">
					<Box className="h-full" variant="white-bordered" slim full hoverable>
						<BoxSection contentClassName="justify-content-center align-items-center text-center">
							<span className="icon-container icon-container-lg">
								<CommunicationSvg className="text-primary" />
							</span>
							<span>{t('userPanel.dashboard.quickLinks.menteeDetails')}</span>
						</BoxSection>
					</Box>
				</Link>
			)}
			{secondStage && (
				<>
					{panelType === 'mentor' && (
						<Link
							to={{ pathname: `${panelScopePath}/pair/${pairId}/final-report` }}
							className="flex-1-mhalf"
						>
							<Box
								className="relative h-full"
								variant={hasFinalReportAlert ? 'white-rose-bordered' : 'white-bordered'}
								slim
								full
								hoverable
							>
								<BoxSection contentClassName="justify-content-center align-items-center text-center">
									<span className="icon-container icon-container-lg">
										<FileChecklistSvg className="text-primary" />
									</span>
									<span>{t('userPanel.dashboard.quickLinks.finalReport')}</span>
								</BoxSection>
								{hasFinalReportAlert && (
									<div className="box-warning-icon">
										<Icon name="exclamation-circle" className="warning-icon-color" />
									</div>
								)}
							</Box>
						</Link>
					)}
					{panelType === 'mentee' && (
						<Link
							to={{
								pathname: `${panelScopePath}/pair/${pairId}/session/last/evaluation-mentee`,
								hash: '#content',
							}}
							className="flex-1-mhalf"
						>
							<Box
								className="h-full"
								variant={hasEvaluationSurveyRequiredAlert ? 'white-rose-bordered' : 'white-bordered'}
								slim
								full
								hoverable
							>
								<BoxSection contentClassName="justify-content-center align-items-center text-center">
									<span className="icon-container icon-container-lg">
										<ArrowCircleSvg className="text-primary" />
									</span>
									<span>{t('userPanel.dashboard.quickLinks.goalsEvaluation')}</span>
								</BoxSection>
								{hasEvaluationSurveyRequiredAlert && (
									<div className="box-warning-icon">
										<Icon name="exclamation-circle" className="warning-icon-color" />
									</div>
								)}
							</Box>
						</Link>
					)}
					<Link
						to={{ pathname: `${panelScopePath}/pair/${pairId}/process-summary` }}
						className="flex-1-mhalf"
					>
						<Box
							className="relative h-full"
							variant={hasProgressStepsAlert ? 'white-rose-bordered' : 'white-bordered'}
							slim
							full
							hoverable
						>
							<BoxSection contentClassName="justify-content-center align-items-center text-center">
								<span className="icon-container icon-container-lg">
									<FileCheckSvg className="text-primary" />
								</span>
								<span>{t('userPanel.dashboard.quickLinks.processSummary')}</span>
							</BoxSection>
							{hasProgressStepsAlert && (
								<div className="box-warning-icon">
									<Icon name="exclamation-circle" className="warning-icon-color" />
								</div>
							)}
						</Box>
					</Link>
				</>
			)}
		</div>
	);
};

type NewQuickLinksPropsewQ = {
	faqArticle: KnowledgeBaseArticleContentItemMeta;
};

const NewQuickLinks = ({ faqArticle: articleData }: NewQuickLinksPropsewQ) => {
	const { t } = useTranslation();

	const { panelScopePath } = usePanel();

	return (
		<div className="flex flex-row flex-wrap gap-2 md:gap-4 px-1">
			<Link to={{ pathname: `${panelScopePath}/pairing` }} className="flex-1-mhalf">
				<Box className="h-full" variant="white-bordered" slim full hoverable>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<UserPlusSvg className="text-rose" />
						</span>
						<span>{t('userPanel.dashboard.quickLinks.pairPropositions')}</span>
					</BoxSection>
				</Box>
			</Link>
			<Link to={{ pathname: `${panelScopePath}/personal-details` }} className="flex-1-mhalf">
				<Box className="h-full" variant="white-bordered" slim full hoverable>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<UserSvg className="text-primary" />
						</span>
						<span>{t('userPanel.dashboard.quickLinks.yourBusinessCard')}</span>
					</BoxSection>
				</Box>
			</Link>
			{articleData && (
				<Link
					to={{
						pathname: `${panelScopePath}/knowledge-base/article/${articleData.id}${
							articleData.slug ? `/${slugify(articleData.slug)}` : ''
						}`,
					}}
					className="flex-1-mhalf"
				>
					<Box className="h-full" variant="white-bordered" slim full hoverable>
						<BoxSection contentClassName="justify-content-center align-items-center text-center">
							<span className="icon-container icon-container-lg">
								<QuestionMarkSquareSvg className="text-primary" />
							</span>
							<span>FAQ</span>
						</BoxSection>
					</Box>
				</Link>
			)}
			<Link
				to={{
					pathname: `${panelScopePath}/knowledge-base`,
				}}
				className="flex-1-mhalf"
			>
				<Box className="h-full" variant="white-bordered" slim full hoverable>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<BookSvg className="text-primary" />
						</span>
						<span>{t('userPanel.dashboard.quickLinks.knowledgeBase')}</span>
					</BoxSection>
				</Box>
			</Link>
		</div>
	);
};

const ProgressSteps = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { organizationName, programName, type } = useParams();

	const { currentUser } = useAuth();
	const { todosLoading } = usePanel();

	const { isLoading, isFetched, currentPairsSteps, commonSteps } = useProgressSteps();

	const stepItemMapper = useCallback(
		(step: Step, activeStep?: number) => {
			const stepNo = step.no;
			const disabled = Boolean(stepNo && stepNo > (activeStep || 0));
			const dataPairId = step.data?.pairId;
			const sessionId = step.data?.sessionId;
			switch (step.type) {
				case 'process_summmary':
					return {
						label: t('userPanel.progressSteps.steps.process_summmary'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/process-summary`,
						disabled,
					};
				case 'after_last_session':
					return {
						label: t('userPanel.progressSteps.steps.after_last_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/process-summary`,
						disabled,
					};
				case 'before_last_session':
					return {
						label: t('userPanel.progressSteps.steps.before_last_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session/${sessionId}`,
						disabled,
					};
				// case 'has_last_session':
				// 	return {
				// 		label: t('userPanel.progressSteps.steps.has_last_session'),
				// 		url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session/${sessionId}/agenda`,
				// 		disabled,
				// 	};
				// case 'has_penultimate_session':
				// 	return {
				// 		label: t('userPanel.progressSteps.steps.has_penultimate_session'),
				// 		url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session/${sessionId}`,
				// 		disabled,
				// 	};
				case 'after_mid_session':
					return {
						label: t('userPanel.progressSteps.steps.after_mid_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session`,
						disabled,
					};
				case 'before_mid_session':
					return {
						label: t('userPanel.progressSteps.steps.before_mid_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session/${sessionId}`,
						disabled,
					};
				case 'after_second_session':
					return {
						label: t('userPanel.progressSteps.steps.after_second_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session`,
						disabled,
					};
				case 'before_second_session':
					return {
						label: t('userPanel.progressSteps.steps.before_second_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/mentee-plan`,
						disabled,
					};
				case 'before_first_session':
					return {
						label: t('userPanel.progressSteps.steps.before_first_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/contract`,
						disabled,
					};
				case 'has_first_session':
					return {
						label: t('userPanel.progressSteps.steps.has_first_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session/${sessionId}`,
						disabled,
					};
				// case 'has_contract':
				// 	return {
				// 		label: t('userPanel.progressSteps.steps.first_session_and_contract'),
				// 		url: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/session`,
				// 		disabled: step.disabled,
				// 	};
				case 'has_entry_meeting':
					return {
						label: t('userPanel.progressSteps.steps.has_entry_meeting'),
						url: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'familiarize_with_program':
					return {
						label: t('userPanel.progressSteps.steps.familiarize_with_program'),
						url: `/${organizationName}/${programName}`,
						disabled,
					};
				case 'has_application_form':
					return {
						label: t('userPanel.progressSteps.steps.has_application_form'),
						url: `/${organizationName}/${programName}/apply-${type}`,
						disabled,
					};
				case 'choosing_mentor':
					return {
						label: t('userPanel.progressSteps.steps.choosing_mentor'),
						url: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'first_session':
					return {
						label: t('userPanel.progressSteps.steps.first_session'),
						url: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'fulfillment_of_contract':
					return {
						label: t('userPanel.progressSteps.steps.fulfillment_of_contract'),
						url: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'fulfillment_of_development_plan':
					return {
						label: (
							<Trans
								t={t}
								i18nKey="userPanel.progressSteps.steps.fulfillment_of_development_plan"
								values={{
									type:
										i18n.resolvedLanguage === 'pl' && type === 'mentor'
											? 'Mentora'
											: capitalizeWord(type ?? ''),
								}}
							/>
						),
						url: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'next_mentoring_sessions':
					return {
						label: t('userPanel.progressSteps.steps.next_mentoring_sessions'),
						url: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'register_in_program':
					return {
						label: t('userPanel.progressSteps.steps.register_in_program'),
						url: `/${organizationName}/${programName}`,
						disabled,
					};
				default:
					return null;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[organizationName, programName, type]
	);

	const transformSteps = useCallback(
		({ steps, activeStep }: Omit<StepsCommonData, 'steps'> & { steps: Step[] | null }) => {
			return (steps || [])
				.map((item) => stepItemMapper(item, activeStep))
				.filter((item) => item?.label)
				.map((item, index) => ({
					...item,
					className: classNames({
						'p-step-prev': index < activeStep,
						'p-step-next': index > activeStep,
					}),
					// disabled: forms ? index : index > activeIndex,
					disabled: false,
				})) as MenuItem[];
		},
		[stepItemMapper]
	);

	const handleStepsSelect = useCallback(
		(index: number, steps: Step[]) => {
			const item = stepItemMapper(steps[index]);
			if (item) {
				navigate({
					pathname: item.url,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[stepItemMapper]
	);

	const { data: articleData, isLoading: articleLoading } = useQuery(
		['KNOWLEDGE_BASE_ARTICLE_LIST_LABEL', { labels: ['faq'], language: currentUser?.language }],
		() => knowledgeBaseApi.getArticlesByLabelsOrIds(['faq']),
		{ enabled: true, initialData: {}, select: (data) => data.faq || null }
	);

	if (isFetched && !commonSteps && !currentPairsSteps) {
		return null;
	}

	if (isLoading || todosLoading || articleLoading) {
		return (
			<div className="flex flex-column gap-2">
				<Skeleton className="inline-block h-2rem" width="40%" />
				<div className="w-full flex flex-row flex-wrap gap-2">
					<Skeleton className="flex-1-mhalf min90hpx" />
					<Skeleton className="flex-1-mhalf min90hpx" />
					<Skeleton className="flex-1-mhalf min90hpx" />
					<Skeleton className="flex-1-mhalf min90hpx" />
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-column gap-3">
			{commonSteps && !currentPairsSteps && (
				<>
					<SubPageTitle title={t('userPanel.progressSteps.welcome')} />
					<NewQuickLinks faqArticle={articleData} />
					<div className="hidden md:flex flex-column overflow-x-auto">
						<Steps
							className="p-steps-progress w-full"
							model={transformSteps(commonSteps)}
							activeIndex={commonSteps.activeStep}
							onSelect={({ index, originalEvent }) => {
								originalEvent.preventDefault();
								handleStepsSelect(index, commonSteps.steps);
							}}
							readOnly={false}
						/>
					</div>
				</>
			)}
			{currentPairsSteps && !commonSteps && (
				<>
					{currentPairsSteps.length === 1 && (
						<>
							<div className="py-3">
								<span className="text-base">
									{t('userPanel.progressSteps.progressWith')}
									<span className="font-bold">{` ${currentPairsSteps[0].label || '-'}`}</span>
								</span>
							</div>
							<NewQuickLinksForPair pairSteps={currentPairsSteps[0]} />
							{currentPairsSteps[0].steps !== null && (
								<div className="hidden md:flex flex-column overflow-x-auto mb-4">
									<Steps
										className="p-steps-progress w-full"
										model={transformSteps(currentPairsSteps[0])}
										activeIndex={currentPairsSteps[0].activeStep}
										onSelect={({ index, originalEvent }) => {
											originalEvent.preventDefault();
											handleStepsSelect(index, currentPairsSteps[0].steps!);
										}}
										readOnly={false}
									/>
								</div>
							)}
						</>
					)}
					{currentPairsSteps.length >= 2 && (
						<>
							<SubPageTitle title={t('userPanel.progressSteps.yourMentoringPairs')} />
							<div className="flex flex-column gap-2">
								{currentPairsSteps.map((steps) => (
									<NewQuickLinksForPair pairSteps={steps} mini />
								))}
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default ProgressSteps;
